import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Factory } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ServiceProductionClient } from "../../api/floor";
import { ServiceOrderClient, type ServiceOrderDTO, type ServiceOrderLineDTO } from "../../api/servicedesk";
import { NexpimView } from "../../views";
import { MakeAtPlantDialog } from "./MakeAtPlantDialog";
import { AvailabilityBadge, useServiceAvailability } from "./ServicePartsPicker";

/** Whole days since an ISO date, today being 0. */
function ageInDays(date: string | null | undefined): number | null {
  if (!date) return null;
  const then = Date.parse(`${date}T00:00:00`);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

/**
 * Every open service line waiting for a part no lot is making yet, across
 * orders: what the desk can send to the plant as one service lot.
 *
 * <p>Sorted by part, so lines waiting for the same panel sit together and are
 * made in one component. Each line says whether the service shelf can cover it
 * (those ship from the shelf rather than being made), how long it has waited,
 * and which order it is for.</p>
 */
export function ServiceProductionQueuePage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const allowed = hasView(NexpimView.SERVICE_DESK);
  const production = useMemo(() => new ServiceProductionClient(http), [http]);
  const orders = useMemo(() => new ServiceOrderClient(http), [http]);
  const [selected, setSelected] = useState<ServiceOrderLineDTO[]>([]);
  const [making, setMaking] = useState(false);

  const queue = useQuery({
    queryKey: ["servicedesk", "production", "queue"],
    queryFn: async () => {
      const lines = await production.queue();
      const ids = [...new Set(lines.map((line) => line.service_order_id))];
      const found = ids.length > 0 ? await orders.findByIds(ids) : [];
      return { lines, orders: new Map<number, ServiceOrderDTO>(found.map((order) => [order.id, order])) };
    },
    enabled: allowed,
  });
  const lines = useMemo(() => queue.data?.lines ?? [], [queue.data]);
  const orderById = useMemo(() => queue.data?.orders ?? new Map<number, ServiceOrderDTO>(), [queue.data]);
  const orderNumbers = useMemo(() => new Map([...orderById.values()].map((order) => [order.id, order.number])), [orderById]);
  const availability = useServiceAvailability(lines.map((line) => line.part_id));

  const cols = useMemo(() => {
    const c = defineColumns<ServiceOrderLineDTO>();
    return [
      c.custom(
        "part_number",
        (row) => row.part_number ?? "",
        (row) => (
          <span>
            <span className="font-medium">{row.part_number}</span>
            <span className="ml-2 text-[11px] text-ink-faint">{row.part_name}</span>
          </span>
        ),
        { headerKey: "column.bom.component", priority: 1 },
      ),
      c.number("quantity_outstanding", (row) => Number(row.quantity_outstanding ?? 0), { headerKey: "sd.plant.left", priority: 1 }),
      c.custom(
        "shelf",
        (row) => availability.get(row.part_id)?.available ?? 0,
        (row) => <AvailabilityBadge availability={availability.get(row.part_id)} needed={Number(row.quantity_outstanding ?? 0)} />,
        { headerKey: "column.sd.shelf", priority: 2 },
      ),
      c.custom(
        "order",
        (row) => orderById.get(row.service_order_id)?.number ?? 0,
        (row) => {
          const order = orderById.get(row.service_order_id);
          return (
            <Link
              to="/servicedesk/orders/$orderId"
              params={{ orderId: String(row.service_order_id) }}
              onClick={(event) => event.stopPropagation()}
              className="text-accent hover:underline"
            >
              {order?.number ?? row.service_order_id}
            </Link>
          );
        },
        { headerKey: "column.sd.number", priority: 1 },
      ),
      c.custom(
        "for",
        (row) => {
          const order = orderById.get(row.service_order_id);
          return order?.kind === "INTERNAL" ? t("sd.kind.internal", "Internal") : (order?.client_name ?? order?.ship_name ?? "");
        },
        (row) => {
          const order = orderById.get(row.service_order_id);
          return order?.kind === "INTERNAL" ? (
            <Badge variant="info" size="sm">
              {t("sd.kind.internal", "Internal")}
            </Badge>
          ) : (
            <span className="text-[12px]">{order?.client_name ?? order?.ship_name ?? ""}</span>
          );
        },
        { headerKey: "column.sd.client", priority: 3 },
      ),
      c.text("motive", (row) => (row.service_motive_code ? `${row.service_motive_code} · ${row.service_motive_name ?? ""}` : ""), {
        headerKey: "column.sd.motive",
        priority: 3,
      }),
      c.text("product_sku", (row) => row.product_sku ?? "", { headerKey: "column.sd.product", priority: 4 }),
      c.number("age", (row) => ageInDays(orderById.get(row.service_order_id)?.order_date) ?? 0, {
        headerKey: "column.sd.waiting-days",
        priority: 2,
      }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", defaultVisible: false }),
    ];
  }, [t, availability, orderById]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.service-production", "Service production")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.service-production", "Service production")}
        subtitle={t("sd.queue.subtitle", "Open service lines waiting for parts no lot is making yet. Tick them to make them at the plant as one lot.")}
      />
      <div className="flex min-h-0 flex-1 flex-col">
        <DataTable<ServiceOrderLineDTO>
          data={lines}
          columns={cols}
          getRowId={(row) => String(row.id)}
          loading={queue.isLoading}
          selectable={canWrite}
          onSelectionChange={setSelected}
          initialSorting={[{ id: "part_number", desc: false }]}
          stateKey="/servicedesk/production"
          actions={
            canWrite
              ? [
                  {
                    label:
                      selected.length > 0
                        ? t("sd.plant.action-n", "Make {{n}} at the plant", { n: selected.length })
                        : t("sd.plant.action", "Make at the plant"),
                    icon: <Factory className="size-4" />,
                    write: true,
                    disabled: selected.length === 0,
                    onClick: () => setMaking(true),
                  },
                ]
              : []
          }
          emptyMessage={t("sd.queue.empty", "No service line is waiting for the plant.")}
        />
      </div>
      {making && <MakeAtPlantDialog lines={selected} orderNumbers={orderNumbers} onClose={() => setMaking(false)} />}
    </>
  );
}
